/**
 * Maps a pathname back to the feature that owns it.
 *
 * This is what makes "track every feature" cost nothing per feature: the
 * sidebar registry already knows every feature's key, label, group, and route
 * prefix, so a new feature is tracked the moment it's registered — no
 * instrumentation to remember, nothing to keep in sync.
 *
 * Matching is longest-prefix over two candidates per feature:
 *   - the nav target itself (`/cro-forecast/summary`)
 *   - its first segment (`/cro-forecast`), but ONLY when exactly one feature
 *     claims that segment
 *
 * The second candidate is what catches sibling routes the nav doesn't point at
 * — `/cro-forecast/deals` belongs to cro_forecast even though the nav links to
 * `/cro-forecast/summary`. The uniqueness condition is what keeps the shared
 * `/system/*` namespace honest: three features live there, so `/system` never
 * becomes a fallback and `/system/roles` only ever matches roles.
 */

import { features } from "@/features/registry";
import type { NavGroup } from "@/features/types";

export interface FeatureRef {
  key: string;
  label: string;
  group: NavGroup | "Other";
}

interface IndexEntry {
  prefix: string;
  ref: FeatureRef;
}

/** Drop TanStack param segments so a stray `/x/$id` nav target indexes as `/x`. */
function staticPrefix(to: string): string {
  const segments = to.split("/");
  const cut = segments.findIndex((s) => s.startsWith("$"));
  return (cut === -1 ? segments : segments.slice(0, cut)).join("/") || "/";
}

function buildIndex(): IndexEntry[] {
  const roots = new Map<string, Set<string>>();
  for (const f of features) {
    const to = staticPrefix(f.nav.to);
    const root = `/${to.split("/")[1] ?? ""}`;
    if (root === "/") continue;
    const owners = roots.get(root) ?? new Set<string>();
    owners.add(f.key);
    roots.set(root, owners);
  }

  const entries: IndexEntry[] = [];
  for (const f of features) {
    const ref: FeatureRef = {
      key: f.key,
      label: f.nav.label,
      group: f.nav.group ?? "Other",
    };
    const to = staticPrefix(f.nav.to);
    if (to === "/") continue;
    entries.push({ prefix: to, ref });

    const root = `/${to.split("/")[1] ?? ""}`;
    if (root !== to && roots.get(root)?.size === 1) {
      entries.push({ prefix: root, ref });
    }
  }

  // Longest prefix wins, so `/system/roles` beats a hypothetical `/system`.
  return entries.sort((a, b) => b.prefix.length - a.prefix.length);
}

let index: IndexEntry[] | null = null;

export function featureForPath(pathname: string): FeatureRef | null {
  index ??= buildIndex();
  for (const { prefix, ref } of index) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return ref;
  }
  return null;
}

/** Test seam — the index is built once from a module-level import. */
export function resetFeatureIndex(): void {
  index = null;
}
